// src/services/resultsService.js
//
// Service layer for the "My Results" / Picks History flow.
//
// The shape of every payload here is taken from the backend brief.
// JSDoc typedefs mirror the TypeScript interfaces in that brief so
// editors give the same autocomplete/hint experience you'd get in TS
// without forcing a .ts conversion of the rest of the app.
//
// File location note: recommended path is `src/services/`. If the
// project already has `src/utils/` as a service folder, dropping this
// file there works too — just update the import in PicksHistory.

import axiosInstance from "../utils/AxiosInstance";

/* ============================================================
   Types (JSDoc) — matches backend DTOs
   ============================================================ */

/**
 * @typedef {"POWER_BALL" | "MEGA_MILLIONS"} TypeLottery
 *
 * @typedef {Object} DrawResponseDto
 * @property {number}      id
 * @property {number|null} value   Jackpot in USD
 * @property {string}      date    YYYY-MM-DD
 *
 * @typedef {Object} BuyDrawResponseDto
 * @property {number}            id
 * @property {string}            date         ISO purchase timestamp
 * @property {number}            creditUsed
 * @property {DrawResponseDto}   draw
 *
 * @typedef {Object} NumbersBuyDrawResponseDto
 * @property {BuyDrawResponseDto} buyDraw
 * @property {number}             num1
 * @property {number}             num2
 * @property {number}             num3
 * @property {number}             num4
 * @property {number}             num5
 * @property {number}             extraBall
 *
 * @typedef {Object} MatchResultStatsResponseDto
 * @property {string}  parameter
 * @property {boolean} bestParameter
 * @property {number}  totalLines
 * @property {number}  totalCollectedUsd
 * @property {number}  averagePrizePerLine
 *
 * Future per-ticket result shape (no endpoint yet — see
 * getGameResultsByBuyDrawId below).
 *
 * @typedef {Object} GameResultResponseDto
 * @property {number}        numbersBuyDrawId
 * @property {number}        buyDrawId
 * @property {number}        drawId
 * @property {number[]}      generatedNumbers
 * @property {number}        generatedExtraBall
 * @property {number[]}      winningNumbers
 * @property {number}        winningExtraBall
 * @property {number}        hits
 * @property {boolean}       extraMatched
 * @property {string|null}   matchedNumbersDetail
 * @property {number}        prizeValueUsd
 * @property {boolean}       winner
 *
 * @typedef {Object} HitDistributionResponseDto
 * @property {number}      hits
 * @property {number}      totalLines
 * @property {number}      totalPrizeUsd
 *
 * @typedef {Object} ExtraBallImpactResponseDto
 * @property {boolean}     extraMatched
 * @property {number}      totalLines
 * @property {number}      totalPrizeUsd
 *
 * @typedef {"PROCESSING"|"PAID_OUT"|"ERROR"|"REFUNDED"} StatusPayment
 *
 * @typedef {Object} TransactionResponseDto
 * @property {number}      id
 * @property {string}      paymentMethod
 * @property {StatusPayment} status
 * @property {string}      transactionType
 * @property {string}      date            ISO transaction timestamp
 * @property {number}      amount
 * @property {string}      idTransaction
 * @property {string|null} creditPlan
 */

/* ============================================================
   API calls
   ============================================================ */

/**
 * Get the user's completed/drawn buy-draw groups for a lottery.
 *
 * The response carries `draw.date` (official drawing date) and
 * `draw.value` (jackpot), but does NOT include the generated game
 * numbers — those have to be fetched per buy-draw via
 * getGeneratedNumbersByBuyDrawId.
 *
 * NOTE: this endpoint only returns buy-draws whose drawing has already
 * happened (the draw scheduler has moved `draw.active` off 0). For a
 * combined "all my picks, pending + drawn" view, prefer
 * getAllGeneratedNumbers below.
 *
 * @param {TypeLottery} typeLottery
 * @returns {Promise<BuyDrawResponseDto[]>}
 */
export async function getCompletedBuyDraws(typeLottery) {
  const res = await axiosInstance.get(
    `/site/buy-draw/list-not-active?typeLottery=${encodeURIComponent(typeLottery)}`
  );
  return Array.isArray(res?.data) ? res.data : [];
}

/**
 * Get EVERY game the user has generated for a lottery, regardless of
 * whether the drawing has happened. Each row in the response carries
 * its full `buyDraw.draw` context (date + jackpot value + the
 * `active` flag once the backend exposes it), so a single call is
 * enough to render the whole picks list.
 *
 * This is the primary endpoint for Dashboard and Picks History.
 * `getCompletedBuyDraws` + per-buy-draw drill-down is still available
 * for code paths that only want drawn results.
 *
 * @param {TypeLottery} typeLottery
 * @returns {Promise<NumbersBuyDrawResponseDto[]>}
 */
export async function getAllGeneratedNumbers(typeLottery) {
  const res = await axiosInstance.get(
    `/site/lotto-analyzer/list-numbers-generated?typeLottery=${encodeURIComponent(typeLottery)}`
  );
  return Array.isArray(res?.data) ? res.data : [];
}

/**
 * Get every generated game line for a single buy-draw.
 *
 * KNOWN BACKEND ISSUE (per the brief): the response may contain
 * `num1: 0` because `NumbersBuyDrawMapper` currently has
 * `@Mapping(target = "num1", ignore = true)`. The frontend deliberately
 * displays whatever the backend returns — we don't substitute fake
 * values. Fix the mapper by removing that `ignore = true` annotation.
 *
 * @param {number} buyDrawId
 * @returns {Promise<NumbersBuyDrawResponseDto[]>}
 */
export async function getGeneratedNumbersByBuyDrawId(buyDrawId) {
  const res = await axiosInstance.get(
    `/site/lotto-analyzer/list-numbers-generated/${encodeURIComponent(buyDrawId)}`
  );
  return Array.isArray(res?.data) ? res.data : [];
}

/**
 * Total USD won by a user across all of their match results. The
 * backend returns a raw scalar, not a JSON object — we coerce
 * defensively in case the deserializer ever changes that.
 *
 * @param {number} userId
 * @returns {Promise<number>}
 */
export async function getTotalUserWinnings(userId) {
  const res = await axiosInstance.get(
    `/site/match-result/total-by-user/${encodeURIComponent(userId)}`
  );
  const raw = res?.data;
  const v = typeof raw === "number" ? raw : parseFloat(raw);
  return Number.isFinite(v) ? v : 0;
}

/**
 * Per-lottery USD total for a user (Powerball or Mega Millions). Used
 * for the per-lottery breakdown on the dashboard. Returns 0 if the
 * endpoint hasn't been called yet for this user / lottery.
 *
 * @param {number} userId
 * @param {TypeLottery} typeLottery
 * @returns {Promise<number>}
 */
export async function getTotalUserWinningsByLottery(userId, typeLottery) {
  const res = await axiosInstance.get(
    `/site/match-result/total-by-user-lottery/${encodeURIComponent(userId)}/${encodeURIComponent(typeLottery)}`
  );
  const raw = res?.data;
  const v = typeof raw === "number" ? raw : parseFloat(raw);
  return Number.isFinite(v) ? v : 0;
}

/**
 * Per-game match results for a single buy-draw. Each row in the
 * response carries:
 *   - numbersBuyDrawId  → key the frontend uses to overlay prize info
 *                         onto the corresponding pick row from
 *                         /lotto-analyzer/list-numbers-generated
 *   - generatedNumbers / generatedExtraBall  → the user's picks
 *   - winningNumbers   / winningExtraBall    → the official drawing
 *   - hits, extraMatched, matchedNumbersDetail, prizeValueUsd, winner
 *
 * Returns an empty array if the buy-draw has no results yet (e.g. the
 * drawing has happened but the match-result scheduler hasn't run on
 * it). 404 still resolves to null so callers can branch on it.
 *
 * @param {number} buyDrawId
 * @returns {Promise<GameResultResponseDto[]|null>}
 */
export async function getGameResultsByBuyDrawId(buyDrawId) {
  try {
    const res = await axiosInstance.get(
      `/site/match-result/by-buy-draw/${encodeURIComponent(buyDrawId)}`
    );
    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404 || status === 501) return null;
    throw err;
  }
}

/**
 * Per-hits-count breakdown of every line the user has ever generated
 * (e.g. how many lines matched 0/1/2/3/4/5 numbers, and how much each
 * bucket paid out in total). Used for the Dashboard "hit distribution"
 * chart. Resolves to an empty array on failure so the UI can render an
 * empty state instead of erroring out.
 *
 * @returns {Promise<HitDistributionResponseDto[]>}
 */
export async function getMyHitDistribution() {
  try {
    const res = await axiosInstance.get("/site/match-result/my-hit-distribution");
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/**
 * Breaks down the user's lines by whether the extra ball (Powerball /
 * Mega Ball) matched, with total lines + total prize per bucket. Used
 * to answer "does buying the extra ball actually pay off for me."
 *
 * @returns {Promise<ExtraBallImpactResponseDto[]>}
 */
export async function getMyExtraBallImpact() {
  try {
    const res = await axiosInstance.get("/site/match-result/my-extra-ball-impact");
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/**
 * The single highest-prize line the user has ever won, for the
 * Dashboard "your best result ever" card. The backend returns 204 (no
 * body) if the user has never won anything — that resolves to `null`
 * here, same as a genuine fetch failure, so callers can render one
 * empty state either way.
 *
 * @returns {Promise<GameResultResponseDto|null>}
 */
export async function getMyBestResult() {
  try {
    const res = await axiosInstance.get("/site/match-result/my-best-result");
    return res?.status === 204 || !res?.data ? null : res.data;
  } catch {
    return null;
  }
}

/**
 * The user's full transaction/purchase history (credit purchases and
 * subscription charges alike), newest first per the backend's
 * `listByUser` ordering. Backs both the Dashboard "total spent" tile
 * and the Settings "Purchases" tab.
 *
 * @returns {Promise<TransactionResponseDto[]>}
 */
export async function getMyTransactions() {
  const res = await axiosInstance.get("/site/payment/my-transactions");
  return Array.isArray(res?.data) ? res.data : [];
}

/**
 * Public (unauthenticated) upcoming-drawing info for both lotteries.
 *
 * Backs the landing page, the dashboard, and the generator so all three
 * show the same authoritative next draw date + estimated jackpot instead
 * of hardcoded values. Safe to call before login — the endpoint is public.
 *
 * Resolves to a map keyed by lottery type for direct lookup:
 *   {
 *     MEGA_MILLIONS: { value, date, target },
 *     POWER_BALL:    { value, date, target },
 *   }
 * where `value` is the jackpot in USD (number|null), `date` is the
 * "YYYY-MM-DD" drawing date (string|null), and `target` is a JS Date for
 * the exact drawing instant (Date|null) suitable for <Countdown>. On any
 * failure it resolves to an empty object so callers can fall back to their
 * own client-side estimates.
 *
 * @returns {Promise<Record<string, { value: number|null, date: string|null, target: Date|null }>>}
 */
export async function getNextDraws() {
  try {
    const res = await axiosInstance.get("/site/public/next-draws");
    const list = Array.isArray(res?.data) ? res.data : [];
    const byType = {};
    for (const d of list) {
      const type = String(d?.typeLottery || "").toUpperCase().trim();
      if (!type) continue;
      const rawValue = d?.nextValue;
      const value =
        rawValue === null || rawValue === undefined ? null : Number(rawValue);
      let target = null;
      if (d?.nextDrawAt) {
        const dt = new Date(d.nextDrawAt);
        if (!Number.isNaN(dt.getTime())) target = dt;
      }
      byType[type] = {
        value: Number.isFinite(value) ? value : null,
        date: d?.nextDate || null,
        target,
      };
    }
    return byType;
  } catch {
    return {};
  }
}

/* ============================================================
   Formatting helpers (UI layer pulls these in directly)
   ============================================================ */

/**
 * Format a jackpot value (raw USD) compactly: 250000000 → "$250M".
 * Returns "—" for null/undefined/NaN so the UI always has a string.
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatJackpot(value) {
  if (value === null || value === undefined) return "—";
  const v = Number(value);
  if (!Number.isFinite(v)) return "—";
  if (v >= 1_000_000_000) {
    const b = v / 1_000_000_000;
    return `$${b.toFixed(b < 10 ? 1 : 0).replace(/\.0$/, "")}B`;
  }
  if (v >= 1_000_000) return `$${Math.round(v / 1_000_000)}M`;
  if (v >= 1_000)     return `$${Math.round(v / 1_000)}K`;
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

/**
 * Format a precise USD amount (used for total winnings, where cents
 * matter). 150 → "$150.00".
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatUsd(value) {
  const v = Number(value);
  if (!Number.isFinite(v)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);
}

/**
 * Format a draw date for display. The backend ships these as
 * "YYYY-MM-DD" strings; we render them as "May 14, 2026" in en-US to
 * match the (English-only) UI, same as formatUsd.
 *
 * NOTE: a date-only "YYYY-MM-DD" passed to `new Date()` is parsed as
 * UTC midnight, which then flips to the previous day in negative-
 * offset timezones (e.g. anything west of UTC). We split the parts
 * and construct a local-midnight Date so the displayed day matches
 * the lottery's announced day everywhere.
 *
 * @param {string|null|undefined} raw
 * @returns {string}
 */
export function formatDrawDate(raw) {
  if (!raw) return "—";
  const ymd = String(raw).split("T")[0];
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const dt = (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d))
    ? new Date(y, m - 1, d)
    : new Date(raw);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

/**
 * Classify a draw as pending or completed.
 *
 * Primary signal: the backend's `draw.active` flag. The backend uses
 * `0` (TypeBoolean.TRUE) for the one open drawing per lottery; once the
 * scheduler pulls the result it rewrites `active` to the draw's own id
 * (any non-zero value = drawn). We trust that absolutely — the drawing
 * scheduler is authoritative.
 *
 * Fallback: compare `draw.date` to today's local date. If today is
 * before the draw date, the drawing is clearly pending. If today is
 * AFTER the draw date, it's drawn. The only ambiguous case is "today
 * IS the draw date" — we mark that as pending too, because drawings
 * happen in the evening and a user checking the dashboard in the
 * morning hasn't had their drawing yet. Once you add `active` to the
 * backend DTO, this fallback stops being exercised.
 *
 * @param {DrawResponseDto|null|undefined} draw
 * @returns {{ pending: boolean, source: "active-flag"|"date-fallback"|"unknown" }}
 */
export function getDrawStatus(draw) {
  if (!draw) return { pending: true, source: "unknown" };

  // Primary signal — backend's active flag if present.
  if (draw.active !== undefined && draw.active !== null) {
    const n = Number(draw.active);
    return { pending: n === 0, source: "active-flag" };
  }

  // Fallback — date comparison.
  const raw = draw.date;
  if (!raw) return { pending: true, source: "unknown" };
  const ymd = String(raw).split("T")[0];
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return { pending: true, source: "unknown" };
  }
  const drawDate = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Pending if today is the draw date or earlier.
  return { pending: drawDate.getTime() >= today.getTime(), source: "date-fallback" };
}

/**
 * Resolve the per-pick "gain" status into UI-ready text + tone.
 *
 * The pick's lifecycle has four states from the user's perspective:
 *
 *   1. Drawing pending      — the lottery hasn't drawn yet.
 *   2. Awaiting results     — the lottery drew, but the match-result
 *                              scheduler hasn't computed our row yet.
 *   3. No match             — drawn and scored, this pick didn't win.
 *   4. Won $X               — drawn, scored, and this pick won money.
 *
 * Returns { label, tone, prize } where:
 *   tone:  "muted" for states 1–3 (greyed text),
 *          "gold"  for state 4 (highlighted)
 *   prize: numeric USD value (0 for non-win states)
 *
 * @param {object} args
 * @param {GameResultResponseDto|null|undefined} args.gameResult
 * @param {boolean} args.drawPending
 * @returns {{ label: string, tone: "muted"|"gold", prize: number }}
 */
export function describeGain({ gameResult, drawPending }) {
  if (drawPending) {
    return { label: "Drawing pending", tone: "muted", prize: 0 };
  }
  if (!gameResult) {
    return { label: "Awaiting results", tone: "muted", prize: 0 };
  }
  const prize = Number(gameResult.prizeValueUsd) || 0;
  if (prize <= 0) {
    return { label: "No match", tone: "muted", prize: 0 };
  }
  return { label: `${formatUsd(prize)} won`, tone: "gold", prize };
}
